/**
 * Hero video diagnostics.
 *
 * Logs the YouTube embed URL, parsed videoId, source/mediaType, and any
 * iframe load/error events to help debug "Fejl 153" (YouTube embed
 * configuration error). Only runs in the browser.
 *
 * Usage:
 *   useHeroVideoDiagnostics({ source: "home" | "banner", mediaType, sourceUrl, youtubeId, embedUrl });
 *   <iframe {...iframeDiagnosticHandlers("home", embedUrl)} />
 */
import { useEffect } from "react";

const PREFIX = "[hero-video]";

export interface HeroDiagnosticsInput {
  source: string;
  mediaType: string | undefined;
  sourceUrl: string;
  youtubeId: string | null;
  embedUrl: string | null;
}

export function useHeroVideoDiagnostics(info: HeroDiagnosticsInput) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    // eslint-disable-next-line no-console
    console.info(`${PREFIX} mount`, {
      source: info.source,
      mediaType: info.mediaType,
      sourceUrl: info.sourceUrl,
      youtubeId: info.youtubeId,
      embedUrl: info.embedUrl,
      origin: window.location.origin,
    });

    // YouTube reports player errors via postMessage on the embed iframe.
    // We listen globally and log any message originating from a YouTube
    // domain that contains an "onError" / errorCode payload.
    const onMessage = (event: MessageEvent) => {
      const origin = event.origin || "";
      if (!/youtube(-nocookie)?\.com$/.test(new URL(origin).host || "")) return;
      let data: unknown = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          /* not JSON, ignore */
        }
      }
      const payload = data as { event?: string; info?: unknown } | null;
      if (payload && (payload.event === "onError" || payload.event === "infoDelivery")) {
        // eslint-disable-next-line no-console
        console.warn(`${PREFIX} youtube postMessage`, {
          source: info.source,
          youtubeId: info.youtubeId,
          embedUrl: info.embedUrl,
          event: payload.event,
          info: payload.info,
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [info.source, info.mediaType, info.sourceUrl, info.youtubeId, info.embedUrl]);
}

export function iframeDiagnosticHandlers(source: string, embedUrl: string | null) {
  return {
    onLoad: () => {
      // eslint-disable-next-line no-console
      console.info(`${PREFIX} iframe load`, { source, embedUrl });
    },
    onError: (e: unknown) => {
      // eslint-disable-next-line no-console
      console.error(`${PREFIX} iframe error`, { source, embedUrl, error: e });
    },
  };
}
